import type { CapabilityPredicate } from '@/features/authentication/types/permissions';
import { defaultUserManagementRoute } from '@/features/user-management/lib/defaultUserManagementRoute';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  Building2,
  Cpu,
  Crosshair,
  Film,
  LayoutDashboard,
  Library,
  ListRestart,
  MapPin,
  Truck,
  UserRound,
  UsersRound,
} from 'lucide-react';
import type { ReactNode } from 'react';

export interface NavigationItem {
  label: ReactNode;
  /** Stable section root used for active matching and mobile section behavior. */
  to: string;
  /** Optional final destination for a primary-nav click. */
  destination?:
    | string
    | ((hasCapability: Parameters<CapabilityPredicate>[0]) => string | undefined);
  icon: ReactNode;
  /**
   * Accessible tooltip label shown when the desktop rail is collapsed to icons.
   * Stored as a lazy Lingui descriptor so the macro emits the same compiled id as
   * the matching `<Trans>` label. `DefaultNavSidebar` resolves it for the active
   * locale at render time with `i18n._(tooltip)`.
   */
  tooltip?: MessageDescriptor;
  /** Pattern matched against the pathname for active state (defaults to `to`). */
  activePattern?: string;
  /** Optional key for background-work activity published by the owning feature. */
  activityId?: string;
  /** Typed global-capability predicate controlling whether this entry is shown. */
  visibleWhen?: CapabilityPredicate;
}

export interface NavigationGroup {
  id: string;
  /**
   * Section header shown above the group's items when the primary rail is
   * expanded (hidden in icon-collapsed mode). Built with `<Trans>` like item labels.
   */
  label: ReactNode;
  items: NavigationItem[];
}

/**
 * Grouped primary-nav entries. `Workspace` holds day-to-day and profile surfaces,
 * `Supervise` holds supervisory tools, and `Admin` holds administration
 * configuration. Items without `requires` show to any authenticated user;
 * gated items appear only when the predicate passes (`useNavigationGroups`).
 *
 */
export const NAVIGATION_GROUPS: NavigationGroup[] = [
  {
    id: 'workspace',
    label: <Trans>Workspace</Trans>,
    items: [
      {
        label: <Trans>Raw Footage</Trans>,
        tooltip: msg`Raw Footage`,
        to: '/raw-footage',
        icon: <Film size={14} />,
        activePattern: '/raw-footage',
        visibleWhen: (has) => has('assets.list'),
      },
      {
        label: <Trans>Transfers</Trans>,
        tooltip: msg`Transfers`,
        to: '/transfers',
        icon: <ListRestart size={14} />,
        activePattern: '/transfers',
        activityId: 'transfers',
        visibleWhen: (has) => has('submissions.list') || has('source-imports.list'),
      },
      {
        label: <Trans>Deliveries</Trans>,
        tooltip: msg`Deliveries`,
        to: '/delivery',
        icon: <Truck size={14} />,
        activePattern: '/delivery',
        visibleWhen: (has) => has('deliveries.manage'),
      },
      {
        label: <Trans>Field Log</Trans>,
        tooltip: msg`Field Log`,
        to: '/field/today',
        icon: <MapPin size={14} />,
        activePattern: '/field',
        visibleWhen: (has) => has('metadata-library.organization.read'),
      },
      {
        label: <Trans>Calibration</Trans>,
        tooltip: msg`Calibration`,
        to: '/calibration',
        destination: '/calibration/workstations',
        icon: <Crosshair size={14} />,
        activePattern: '/calibration',
        visibleWhen: (has) => has('calibration.read') || has('calibration.program.read'),
      },
      {
        label: <Trans>Profile</Trans>,
        tooltip: msg`Profile`,
        to: '/profile/account',
        icon: <UserRound size={14} />,
        activePattern: '/profile',
      },
    ],
  },
  {
    id: 'supervise',
    label: <Trans>Supervise</Trans>,
    items: [
      {
        label: <Trans>Devices</Trans>,
        tooltip: msg`Devices`,
        to: '/devices',
        destination: '/devices/list',
        icon: <Cpu size={14} />,
        activePattern: '/devices',
        visibleWhen: (has) => has('devices.read'),
      },
      {
        label: <Trans>Supervisor</Trans>,
        tooltip: msg`Supervisor`,
        to: '/supervisor/team',
        icon: <LayoutDashboard size={14} />,
        activePattern: '/supervisor',
        visibleWhen: (has) =>
          has('metadata-library.organization.read') ||
          has('assets.list') ||
          has('manual-task.requests.read'),
      },
    ],
  },
  {
    id: 'admin',
    label: <Trans>Admin</Trans>,
    items: [
      {
        label: <Trans>Organizations</Trans>,
        tooltip: msg`Organizations`,
        to: '/admin/organizations',
        icon: <Building2 size={14} />,
        activePattern: '/admin/organizations',
        visibleWhen: (has) => has('organizations.list'),
      },
      {
        label: <Trans>User Management</Trans>,
        tooltip: msg`User Management`,
        to: '/user-management',
        destination: defaultUserManagementRoute,
        icon: <UsersRound size={14} />,
        activePattern: '/user-management',
        visibleWhen: (has) =>
          has('users.list') ||
          has('authz.permission-sets.list') ||
          (has('organization-members.list') && has('organizations.list')),
      },
      {
        label: <Trans>Metadata</Trans>,
        tooltip: msg`Metadata`,
        to: '/metadata',
        destination: '/metadata/environments',
        icon: <Library size={14} />,
        activePattern: '/metadata',
        visibleWhen: (has) =>
          has('metadata-library.organization.read') || has('metadata-library.list'),
      },
    ],
  },
];

/** Flat list of every registered nav item (tests / skeletons). */
export const NAVIGATION_ITEMS: NavigationItem[] = NAVIGATION_GROUPS.flatMap((group) => group.items);
